import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package } from "lucide-react";
import { fetchTicketOrderContext, fetchTicketProblemTypes } from "../../api/TicketAPIs";
import { fetchDesignByCode, fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypes } from "../../api/TypeAPIs";
import { createReplacementOrder } from "../../api/ReplacementAPIs";
import { resolveMediaUrl } from "../../pages/Tickets/ticketUi";

const newKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `repl-${Date.now()}`;

const typeIdOf = (type) => type?.typeId ?? type?.TypeId;
const typeLabel = (type) => {
  const name = type?.name ?? type?.Name ?? "";
  const brand = type?.brand?.name ?? type?.brandName ?? type?.Brand?.Name ?? "";
  return brand ? `${brand} / ${name}` : name;
};

const emptyExtra = () => ({
  key: newKey(),
  typeId: "",
  designCode: "",
  designName: "",
  imageUrl: "",
  quantity: 1,
  customNameAr: "",
  customNameEn: "",
  error: "",
});

const pickField = (obj, ...keys) => {
  if (!obj) return "";
  for (const key of keys) {
    const value = obj[key];
    if (value != null && String(value).trim() !== "") return value;
  }
  return "";
};

const designImageFrom = (design) =>
  pickField(design, "imageUrl_1", "ImageUrl_1", "imageUrl_2", "ImageUrl_2", "imageUrl", "ImageUrl");

const ItemThumb = ({ src, alt = "" }) => {
  const imageSrc = resolveMediaUrl(src);
  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        alt={alt}
        className="w-16 h-16 rounded-lg object-cover bg-gray-100 shrink-0"
      />
    );
  }
  return (
    <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
      <Package size={22} />
    </div>
  );
};

const ReplacementOrderWizard = ({
  open,
  orderId,
  ticketId = null,
  problemReason = "",
  problemTypeId = null,
  onClose,
  onCreated,
}) => {
  const idempotencyKeyRef = useRef(newKey());
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState({});
  const [extras, setExtras] = useState([]);
  const [reasonId, setReasonId] = useState(problemTypeId ? String(problemTypeId) : "");
  const [manualTotal, setManualTotal] = useState("0");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [hydratedImages, setHydratedImages] = useState({});

  const { data: context, isLoading } = useQuery({
    queryKey: ["replacementOrderContext", orderId],
    queryFn: () => fetchTicketOrderContext(orderId),
    enabled: open && !!orderId,
  });

  const { data: types = [] } = useQuery({
    queryKey: ["phoneTypes"],
    queryFn: fetchTypes,
    enabled: open,
  });

  const { data: problemTypes = [] } = useQuery({
    queryKey: ["ticketProblemTypes", "active"],
    queryFn: () => fetchTicketProblemTypes(true),
    enabled: open && !ticketId,
  });

  useEffect(() => {
    if (!open) return;
    idempotencyKeyRef.current = newKey();
    setStep(1);
    setSelected({});
    setExtras([]);
    setReasonId(problemTypeId ? String(problemTypeId) : "");
    setManualTotal("0");
    setError("");
    setSubmitting(false);
    setResult(null);
    setHydratedImages({});
  }, [open, orderId, ticketId, problemTypeId]);

  const items = useMemo(() => {
    const raw = context?.items || context?.Items || [];
    return raw.map((item) => ({
      ...item,
      orderDetailsId: item.orderDetailsId ?? item.OrderDetailsId,
      designId: item.designId ?? item.DesignId,
      typeId: item.typeId ?? item.TypeId,
      productId: item.productId ?? item.ProductId,
      productVariantId: item.productVariantId ?? item.ProductVariantId,
      caseStyleId: item.caseStyleId ?? item.CaseStyleId,
      quantity: item.quantity ?? item.Quantity ?? 1,
      designCode: pickField(item, "designCode", "DesignCode"),
      designName: pickField(item, "designName", "DesignName"),
      imageUrl: pickField(item, "imageUrl", "ImageUrl"),
      productName: pickField(item, "productName", "ProductName"),
      variantCode: pickField(item, "variantCode", "VariantCode"),
      productModel: pickField(item, "productModel", "ProductModel"),
      customNameAr: pickField(item, "customNameAr", "CustomNameAr"),
      customNameEn: pickField(item, "customNameEn", "CustomNameEn"),
    }));
  }, [context]);

  useEffect(() => {
    if (!open || !items.length) return undefined;
    let cancelled = false;
    const hydrate = async () => {
      const updates = {};
      await Promise.all(
        items.map(async (item) => {
          if (item.imageUrl) return;
          try {
            const design = item.designId
              ? await fetchDesignById(item.designId)
              : item.designCode
                ? await fetchDesignByCode(encodeURIComponent(item.designCode))
                : null;
            const url = designImageFrom(design);
            if (url) updates[item.orderDetailsId] = url;
          } catch {
            /* keep placeholder */
          }
        })
      );
      if (!cancelled && Object.keys(updates).length) {
        setHydratedImages((prev) => ({ ...prev, ...updates }));
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [open, items]);
  const address = context?.address;

  const summary = useMemo(() => {
    const originals = items
      .filter((item) => selected[item.orderDetailsId]?.checked)
      .map((item) => {
        const row = selected[item.orderDetailsId];
        const changed = !!(row.designCode && row.designCode.trim() && row.designCode.trim() !== (item.designCode || ""));
        return {
          ...item,
          quantity: Number(row.quantity) || item.quantity,
          designCode: changed ? row.designCode.trim() : item.designCode,
          designName: changed ? row.designName || row.designCode : item.designName,
          changed,
          typeId: row.typeId || item.typeId,
        };
      });
    return { originals, extras: extras.filter((row) => row.designCode.trim() && row.typeId) };
  }, [items, selected, extras]);

  if (!open) return null;

  const toggleItem = (item) => {
    setSelected((prev) => {
      const current = prev[item.orderDetailsId];
      if (current?.checked) {
        const next = { ...prev };
        delete next[item.orderDetailsId];
        return next;
      }
      return {
        ...prev,
        [item.orderDetailsId]: {
          checked: true,
          quantity: item.quantity,
          designCode: "",
          designName: "",
          imageUrl: item.imageUrl || hydratedImages[item.orderDetailsId] || "",
          typeId: item.typeId || "",
        },
      };
    });
  };

  const lookupDesign = async (code) => {
    const trimmed = (code || "").trim();
    if (!trimmed) throw new Error("كود التصميم مطلوب.");
    const design = await fetchDesignByCode(encodeURIComponent(trimmed));
    if (!design) throw new Error("كود التصميم غير موجود.");
    return design;
  };

  const validateStepOne = async () => {
    if (summary.originals.length + extras.length === 0) {
      setError("اختَر صنفاً واحداً على الأقل.");
      return false;
    }
    for (const row of extras) {
      if (!row.typeId || !row.designCode.trim() || !(Number(row.quantity) > 0)) {
        setError("كل صنف إضافي يحتاج موديلاً وكود تصميم وكمية.");
        return false;
      }
      try {
        await lookupDesign(row.designCode);
      } catch (err) {
        setError(err.message || "كود التصميم غير موجود.");
        return false;
      }
    }
    for (const item of items) {
      const row = selected[item.orderDetailsId];
      if (!row?.checked) continue;
      if (!(Number(row.quantity) > 0)) {
        setError("الكمية لازم تكون أكبر من صفر.");
        return false;
      }
      if (row.designCode?.trim() && row.designCode.trim() !== (item.designCode || "")) {
        try {
          const design = await lookupDesign(row.designCode);
          setSelected((prev) => ({
            ...prev,
            [item.orderDetailsId]: {
              ...prev[item.orderDetailsId],
              designName: design.name || design.Name || design.code || design.Code,
              imageUrl: designImageFrom(design) || prev[item.orderDetailsId]?.imageUrl,
            },
          }));
        } catch (err) {
          setError(err.message || "كود التصميم غير موجود.");
          return false;
        }
      }
    }
    setError("");
    return true;
  };

  const goNext = async () => {
    const ok = await validateStepOne();
    if (ok) setStep(2);
  };

  const submit = async () => {
    if (!ticketId && !reasonId) {
      setError("سبب المشكلة مطلوب.");
      return;
    }
    const total = manualTotal === "" ? 0 : Number(manualTotal);
    if (!Number.isFinite(total) || total < 0) {
      setError("سعر الاستبدال لا يمكن أن يكون سالباً.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const lines = [
        ...summary.originals.map((item) => ({
          sourceOrderDetailsId: item.orderDetailsId,
          quantity: Number(selected[item.orderDetailsId].quantity),
          typeId: Number(selected[item.orderDetailsId].typeId || item.typeId) || null,
          designCode: item.changed ? item.designCode : null,
          designId: null,
          caseStyleId: item.caseStyleId || null,
          productId: item.productId || null,
          productVariantId: item.productVariantId || null,
          customNameAr: item.customNameAr || null,
          customNameEn: item.customNameEn || null,
        })),
        ...extras.map((row) => ({
          quantity: Number(row.quantity),
          typeId: Number(row.typeId),
          designCode: row.designCode.trim(),
          customNameAr: row.customNameAr || null,
          customNameEn: row.customNameEn || null,
        })),
      ];
      const created = await createReplacementOrder({
        originalOrderId: Number(orderId),
        ticketId: ticketId ? Number(ticketId) : null,
        problemTypeId: ticketId ? null : Number(reasonId),
        idempotencyKey: idempotencyKeyRef.current,
        manualTotal: total,
        lines,
      });
      setResult(created);
      if (onCreated) onCreated(created);
    } catch (err) {
      setError(err.message || "تعذر إنشاء طلب الاستبدال.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" dir="rtl">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl md:rounded-3xl shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">إنشاء بديل</h2>
            <p className="text-sm text-gray-500">طلب أصلي #{orderId}{ticketId ? ` · تذكرة #${ticketId}` : ""}</p>
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1 rounded-lg bg-gray-100">إغلاق</button>
        </div>

        <div className="p-5 space-y-4">
          {isLoading && <p className="text-gray-500">جاري تحميل أصناف الطلب...</p>}
          {error && <div className="bg-red-50 text-red-700 rounded-xl px-4 py-3">{error}</div>}

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-2">
              <p className="font-bold text-emerald-800">تم إنشاء طلب الاستبدال #{result.replacementOrderId}</p>
              <p className="text-sm text-emerald-900">مرتبط بالطلب الأصلي #{result.originalOrderId}. السعر {result.totalAmount}.</p>
              <p className="text-sm text-emerald-900">واتساب: {({ Sent: "تم الإرسال", Failed: "فشل الإرسال", Sending: "جاري الإرسال" })[result.whatsAppStatus] || result.whatsAppStatus || "غير معروف"}{result.whatsAppError ? ` — ${result.whatsAppError}` : ""}</p>
              <div className="flex flex-wrap gap-4 mt-2">
                <Link to={`/search-orders?orderId=${result.replacementOrderId}`} className="text-indigo-700 font-bold">فتح الطلب #{result.replacementOrderId}</Link>
                <Link to="/replacement-orders" className="text-indigo-700 font-bold">فتح طلبات الاستبدال</Link>
              </div>
            </div>
          )}

          {!result && step === 1 && !isLoading && (
            <>
              <p className="text-sm text-gray-600">اختَر الأصناف المطلوب استبدالها. لا يتم نسخ كل الطلب تلقائيًا.</p>
              <div className="space-y-3">
                {items.map((item) => {
                  const row = selected[item.orderDetailsId];
                  const checked = !!row?.checked;
                  const imageSrc = row?.imageUrl || item.imageUrl || hydratedImages[item.orderDetailsId];
                  return (
                    <div key={item.orderDetailsId} className={`rounded-2xl border p-4 ${checked ? "border-indigo-300 bg-indigo-50" : "border-gray-200"}`}>
                      <label className="flex items-start gap-3">
                        <input type="checkbox" checked={checked} onChange={() => toggleItem(item)} className="mt-1" />
                        <ItemThumb src={imageSrc} alt={item.designName || item.designCode || ""} />
                        <span>
                          <span className="font-bold text-gray-900 block">{item.designName || item.designCode || item.productName || "صنف"}</span>
                          <span className="text-sm text-gray-600 block">{item.designCode ? `كود ${item.designCode}` : item.variantCode || ""} · {item.productModel || "بدون موديل"} · كمية {item.quantity}</span>
                        </span>
                      </label>
                      {checked && (
                        <div className="mt-3 grid md:grid-cols-2 gap-3">
                          <label className="text-sm">
                            الكمية
                            <input
                              type="number"
                              min="1"
                              value={row.quantity}
                              onChange={(e) => setSelected((prev) => ({ ...prev, [item.orderDetailsId]: { ...row, quantity: e.target.value } }))}
                              className="mt-1 w-full border rounded-xl px-3 py-2"
                            />
                          </label>
                          <label className="text-sm">
                            الموديل
                            <select
                              value={row.typeId || ""}
                              onChange={(e) => setSelected((prev) => ({ ...prev, [item.orderDetailsId]: { ...row, typeId: e.target.value } }))}
                              className="mt-1 w-full border rounded-xl px-3 py-2"
                            >
                              <option value={item.typeId || ""}>{item.productModel || "الموديل الأصلي"}</option>
                              {types.map((type) => (
                                <option key={typeIdOf(type)} value={typeIdOf(type)}>{typeLabel(type)}</option>
                              ))}
                            </select>
                          </label>
                          <div className="md:col-span-2">
                            <button
                              type="button"
                              className="text-sm font-bold text-indigo-700"
                              onClick={() => setSelected((prev) => ({
                                ...prev,
                                [item.orderDetailsId]: { ...row, editingDesign: !row.editingDesign },
                              }))}
                            >
                              تغيير التصميم
                            </button>
                            {row.editingDesign && (
                              <input
                                value={row.designCode}
                                placeholder="كود التصميم الجديد"
                                onChange={(e) => setSelected((prev) => ({
                                  ...prev,
                                  [item.orderDetailsId]: { ...row, designCode: e.target.value },
                                }))}
                                onBlur={async () => {
                                  const code = (row.designCode || "").trim();
                                  if (!code) return;
                                  try {
                                    const design = await lookupDesign(code);
                                    setSelected((prev) => ({
                                      ...prev,
                                      [item.orderDetailsId]: {
                                        ...prev[item.orderDetailsId],
                                        designName: design.name || design.Name || code,
                                        imageUrl: designImageFrom(design),
                                      },
                                    }));
                                  } catch {
                                    /* validation happens on next */
                                  }
                                }}
                                className="mt-2 w-full border rounded-xl px-3 py-2"
                              />
                            )}
                            {row.designCode && <p className="text-xs text-amber-700 mt-1">التصميم سيتغير إلى {row.designCode}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t pt-4 space-y-3">
                <h3 className="font-bold">أصناف إضافية</h3>
                {extras.map((row) => (
                  <div key={row.key} className="rounded-2xl border border-amber-200 bg-amber-50 p-4 grid md:grid-cols-2 gap-3">
                    <div className="md:col-span-2 flex items-center gap-3">
                      <ItemThumb src={row.imageUrl} alt={row.designCode || ""} />
                      <span className="text-sm text-amber-900">{row.designName || row.designCode || "صنف إضافي"}</span>
                    </div>
                    <label className="text-sm">الموديل
                      <select value={row.typeId} onChange={(e) => setExtras((prev) => prev.map((x) => x.key === row.key ? { ...x, typeId: e.target.value } : x))} className="mt-1 w-full border rounded-xl px-3 py-2">
                        <option value="">اختر الموديل</option>
                        {types.map((type) => <option key={typeIdOf(type)} value={typeIdOf(type)}>{typeLabel(type)}</option>)}
                      </select>
                    </label>
                    <label className="text-sm">كود التصميم
                      <input
                        value={row.designCode}
                        onChange={(e) => setExtras((prev) => prev.map((x) => x.key === row.key ? { ...x, designCode: e.target.value } : x))}
                        onBlur={async () => {
                          const code = (row.designCode || "").trim();
                          if (!code) return;
                          try {
                            const design = await lookupDesign(code);
                            setExtras((prev) => prev.map((x) => x.key === row.key
                              ? {
                                  ...x,
                                  designName: design.name || design.Name || code,
                                  imageUrl: designImageFrom(design),
                                }
                              : x));
                          } catch {
                            /* validation happens on next */
                          }
                        }}
                        className="mt-1 w-full border rounded-xl px-3 py-2"
                      />
                    </label>
                    <label className="text-sm">الكمية
                      <input type="number" min="1" value={row.quantity} onChange={(e) => setExtras((prev) => prev.map((x) => x.key === row.key ? { ...x, quantity: e.target.value } : x))} className="mt-1 w-full border rounded-xl px-3 py-2" />
                    </label>
                    <label className="text-sm">الاسم بالعربي
                      <input value={row.customNameAr} onChange={(e) => setExtras((prev) => prev.map((x) => x.key === row.key ? { ...x, customNameAr: e.target.value } : x))} className="mt-1 w-full border rounded-xl px-3 py-2" />
                    </label>
                    <label className="text-sm">الاسم بالإنجليزي
                      <input value={row.customNameEn} onChange={(e) => setExtras((prev) => prev.map((x) => x.key === row.key ? { ...x, customNameEn: e.target.value } : x))} className="mt-1 w-full border rounded-xl px-3 py-2" />
                    </label>
                    <button type="button" className="text-red-600 text-sm self-end" onClick={() => setExtras((prev) => prev.filter((x) => x.key !== row.key))}>حذف</button>
                  </div>
                ))}
                <button type="button" onClick={() => setExtras((prev) => [...prev, emptyExtra()])} className="px-4 py-2 rounded-xl bg-amber-100 text-amber-900 font-bold">
                  + إضافة صنف آخر
                </button>
              </div>

              <div className="flex justify-end">
                <button type="button" onClick={goNext} className="px-5 py-2 rounded-xl bg-indigo-600 text-white font-bold">التالي</button>
              </div>
            </>
          )}

          {!result && step === 2 && (
            <div className="space-y-4">
              <div className="rounded-2xl bg-slate-50 p-4">
                <h3 className="font-bold mb-2">بيانات العميل</h3>
                <p>{context?.name} · {context?.phoneNumber}</p>
                <p>{address?.state} · {address?.city}</p>
                <p>{address?.fullAddress}</p>
              </div>
              <div className="rounded-2xl border p-4 space-y-2">
                <h3 className="font-bold">الأصناف</h3>
                {summary.originals.map((item) => (
                  <div key={item.orderDetailsId} className="flex items-center gap-3 text-sm">
                    <ItemThumb src={selected[item.orderDetailsId]?.imageUrl || item.imageUrl || hydratedImages[item.orderDetailsId]} alt={item.designCode || ""} />
                    <p>
                      {item.productModel} · {item.designCode || item.designName} · ×{item.quantity}
                      {item.changed ? " · تم تغيير التصميم" : " · التصميم الأصلي"}
                    </p>
                  </div>
                ))}
                {summary.extras.map((row) => (
                  <div key={row.key} className="flex items-center gap-3 text-sm text-amber-800">
                    <ItemThumb src={row.imageUrl} alt={row.designCode || ""} />
                    <p>إضافة: موديل {row.typeId} · {row.designCode} · ×{row.quantity}</p>
                  </div>
                ))}
              </div>
              <label className="block text-sm font-bold">
                سعر الطلب (افتراضي صفر)
                <input type="number" min="0" step="0.01" value={manualTotal} onChange={(e) => setManualTotal(e.target.value)} className="mt-1 w-full border rounded-xl px-3 py-2" />
              </label>
              {ticketId ? (
                <p className="text-sm bg-indigo-50 rounded-xl px-3 py-2">سبب المشكلة من التذكرة: {problemReason || "محفوظ على التذكرة"}</p>
              ) : (
                <label className="block text-sm font-bold">
                  سبب المشكلة
                  <select value={reasonId} onChange={(e) => setReasonId(e.target.value)} className="mt-1 w-full border rounded-xl px-3 py-2">
                    <option value="">اختر السبب</option>
                    {problemTypes.map((reason) => (
                      <option key={reason.id || reason.Id} value={reason.id || reason.Id}>{reason.name || reason.Name}</option>
                    ))}
                  </select>
                </label>
              )}
              <p className="text-sm text-gray-600">سيتم إنشاء طلب جديد وربطه بالطلب الأصلي #{orderId}. لن يتم تعديل أصناف الطلب الأصلي.</p>
              <div className="flex gap-2 justify-between">
                <button type="button" onClick={() => setStep(1)} className="px-4 py-2 rounded-xl bg-gray-100">رجوع</button>
                <button type="button" disabled={submitting} onClick={submit} className="px-5 py-2 rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-50">
                  {submitting ? "جاري الإنشاء..." : "إنشاء طلب الاستبدال"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReplacementOrderWizard;
